<script lang="ts" setup>
import countTo from 'vue-count-to'
import { computed, onUnmounted, ref } from 'vue'
import { subscribeConfig as rocnovelConfig } from './configs/rocnovel'
import { subscribeConfig as ocereadConfig } from './configs/oceread'
import { subscribeConfig as ficmoonConfig } from './configs/ficmoon'
import { VIP_TYPE } from '@/constants'
import { useSubscript } from '@/composables/useSubscript'
import { loadBrandConfig } from '@/utils/loadBrandConfig'
import { brand, colors } from '@/config/brands'

// TODO: 下一一定要重构, 逻辑太混杂了

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
})

const { subscribeProducts, getUnitPrice, onSubscribeProducts, getIsImageBackground, isZero } = useSubscript('store')
const active = ref(0)
const discountModel = ref(false)

const price = computed(() => {
  if (subscribeProducts.value && subscribeProducts.value[active.value]) {
    const item = subscribeProducts.value[active.value]
    if (isZero(item.preferentialPrice))
      return 'free'
    else if (item.preferentialPrice)
      return item.preferentialPrice
    else
      return item.price
  }
  return null
})

const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount: any = ref(null)
const discountNumber = ref(0)
const getDiscountPercentage = function () {
  if (subscribeProducts.value && subscribeProducts.value.length > 0) {
    let maxDiscount = 0
    let current = null
    subscribeProducts.value.forEach((item) => {
      let discount = 0
      let shouldCalculate = false

      // 畅读会员：只有在 preferentialPrice 存在时才计算折扣(包括为0的情况)
      if (item.subscribeType === VIP_TYPE.ALL && (item.preferentialPrice !== undefined && item.preferentialPrice !== null)) {
        discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
        shouldCalculate = true
      }

      // 折扣会员：有 discountOriginalPrice 就计算折扣
      if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
        discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
        shouldCalculate = true
      }

      // 只有符合条件的会员才参与比较
      if (shouldCalculate && discount >= maxDiscount) {
        current = item
        maxDiscount = Math.round(discount)
      }
    })

    // 只要找到符合条件的会员就返回，不管折扣是多少(包括0)
    if (current)
      return [current, maxDiscount]
  }

  return false
}

function initDiscountModel() {
  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      clearTimeout(timer.value!)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}
initDiscountModel()

function buyNow() {
  onSubscribeProducts(currentDiscount.value)
}

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
})

// 定义样式变量供 CSS v-bind 使用
const redGradient = config.colors.redGradient
const whiteColor = '#FFFFFF'
const purpleTextGradient = config.colors.purpleTextGradient
const vipBgUrl = `url(${config.resources.vipBg})`
const primaryColor = colors?.primary

const subscribeText1 = config.colors.subscribeText1
const subscribeText2 = config.colors.subscribeText2
const subscribeText3 = config.colors.subscribeText3
const subscribeText4 = config.colors.subscribeText4
</script>

<template>
  <div class="subscribe-cover">
    <BasicNavbar class="color-n-text-2" />

    <div class="px-20px">
      <div class="subscribe-text-2 text-20px font-bold h-44px flex items-center justify-center">
        {{ config.texts.membership }}
      </div>
      <div class="text-28px font-bold subscribe-text-1 mt-2">
        {{ config.texts.vipMembership }}
      </div>
      <div class="subscribe-text-3 text-14px">
        {{ config.texts.membershipDesc }}
      </div>
      <div class="subscribe-text-1 mt-2px text-14px">
        {{ config.texts.notJoinedYet }}
      </div>
    </div>

    <div class="member-wrap mt-2 mx-10px bg-n-bg-card b-rd-2 box-shadow px-10px pt-16px">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="px-14px pt-20px pb-16px border-rd-12px subscribe-text-2 relative mb-10px"
          :style="{
            border: active === index ? `1px solid ${primaryColor}` : `1px solid ${colors?.border.primary}`,
            background: getIsImageBackground(item)
              ? `url(${config.resources.memberSubscribeBg}) no-repeat`
              : colors.border.primary,
            backgroundSize: '100% 100%',
            color: getIsImageBackground(item) ? subscribeText4 : subscribeText2,
          }"
          @click="active = index"
        >
          <!-- 角标, 两种会员都有 -->
          <div v-if="item.cornerTip && item.marketingCopy" class="popular-tag">
            {{ item.marketingCopy }}
          </div>

          <!-- 折扣会员 -->
          <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
            <div class="absolute top-0 right-0 font-bold text-28px leading-[28px] cycleBg">
              {{ item.cycleStr }}
            </div>

            <img v-if="brand !== 'ficmoon'" class="flex-shrink-0 mr-16px" :src="config.resources.vipDiscountIcon" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.discountCoins }} Coins
              </div>
              <ul
                class="text-11px list-disc pl-3"
                :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-1'"
              >
                <li>Get {{ item.discountOnceCoins }} Coins at once</li>
                <li>Earn {{ item.discountBonusCoins }} Coins ({{ item.discountDailyCoins }}/day) from daily login</li>
              </ul>
            </div>
            <div class="flex-shrink-0 text-right">
              <div class="text-18px font-bold">
                <div class="min-w-60px">
                  $ <countTo
                    :start-val="Number(item.discountOriginalPrice)"
                    :end-val="Number(item.preferentialPrice || item.price)"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
              </div>
              <div class="text-12px line-through" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">
                $ {{ item.discountOriginalPrice }}
              </div>
            </div>
          </div>

          <!-- 畅读会员 -->
          <div v-else class="flex items-center">
            <img v-if="brand !== 'ficmoon'" class="flex-shrink-0 mr-16px" :src="config.resources.vipIcon" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.cycleStr }}
              </div>
              <div class="subscribe-text-1">
                {{ item.subscribeText }}
              </div>
            </div>

            <div class="flex-shrink-0 text-right">
              <!-- 免费 -->
              <div v-if="isZero(item.preferentialPrice)">
                <div class="text-18px font-bold">
                  {{ config.texts.free }}
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </div>
              <!-- 无优惠价格 -->
              <div v-else-if="!item.preferentialPrice">
                <div class="text-18px font-bold">
                  <span>$ {{ item.price }}</span>
                </div>
                <div v-if="getUnitPrice(item)" class="subscribe-text-3 text-12px">
                  $ {{ getUnitPrice(item) }}
                </div>
              </div>
              <!-- 有优惠价格 -->
              <div v-else>
                <div class="text-18px font-bold">
                  <div class="min-w-60px">
                    $ <countTo
                      :start-val="Number(item.price)"
                      :end-val="Number(item.preferentialPrice)"
                      :duration="3000"
                      :decimals="2"
                    />
                  </div>
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4px text-10px text-n-text-3 pb-80px">
        <p class="text-12px mt-6px">
          {{ config.texts.tips }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip1 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip2 }}
        </p>
      </div>

      <div
        v-if="price"
        class="w-78% ml-11% h-40px bg-n-primary text-n-text-inverse text-16px font-medium flex items-center justify-center border-rd-20px fixed bottom-20px left-0 right-0"
        @click="onSubscribeProducts(subscribeProducts[active])"
      >
        <div v-if="price === 'free'">
          {{ config.texts.free }}
        </div>
        <div v-else>
          {{ config.texts.buyNow }}  <span>$ {{ price }}</span>
        </div>
      </div>
    </div>

    <BasicModal v-model="discountModel" :transparent="true" class-name="discount-modal">
      <div @click="buyNow">
        <div class="w-[200px] flex justify-center absolute top-[40px] left-[50%] ml-[-100px]">
          <div class="text-white text-30px font-bold italic">
            {{ discountNumber }}<span class="text-20px">%</span>
            <div class="relative top-[-5px]">
              OFF
            </div>
          </div>
        </div>
        <img :src="config.resources.discountBg" width="300px">
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.member-wrap {
  min-height: calc(100vh - 192px);
}

.subscribe-cover {
  width: 100%;
  background: v-bind(vipBgUrl) no-repeat top left;
  background-size: 100% auto;
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 56px;
  height: 18px;
  background: v-bind(redGradient);
  border-radius: 8px 0px 12px 0px;
  font-weight: 400;
  font-size: 10px;
  color: v-bind(whiteColor);
  text-align: center;
}
.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.subscribe-text-1 {
  color: v-bind(subscribeText1);
}
.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-text-3 {
  color: v-bind(subscribeText3);
}
</style>

<style lang="scss">
.discount-modal {
  .close-icon {
    top: 12px;
    right: 0;
  }
}
</style>
